import type { ISwimlaneHeaderProps } from '@/lib/api/types/roadmapping';

/**
 * Applies custom order to headers based on saved order array.
 *
 * Headers that appear in the savedOrder array are placed first, in that order.
 * Headers not in the savedOrder array are moved to the end.
 * If savedOrder is null or empty, headers are returned unchanged.
 *
 * @param headers - Array of swimlane header objects to reorder
 * @param savedOrder - Array of header IDs in desired order, or null for default order
 * @returns Reordered array of headers
 *
 * @example
 * const headers = [
 *   { id: '1', label: 'To Do' },
 *   { id: '2', label: 'In Progress' },
 *   { id: '3', label: 'Done' }
 * ];
 * const order = ['3', '1', '2'];
 * applyHeaderOrder(headers, order);
 * // Returns: [{ id: '3', ... }, { id: '1', ... }, { id: '2', ... }]
 */
export function applyHeaderOrder(
  headers: ISwimlaneHeaderProps[],
  savedOrder: string[] | null,
): ISwimlaneHeaderProps[] {
  // No custom order specified, return headers as-is
  if (!savedOrder || savedOrder.length === 0) {
    return headers;
  }

  // Create a map for O(1) lookup of headers by ID
  const headerMap = new Map(headers.map((h) => [h.id, h]));
  const orderedHeaders: ISwimlaneHeaderProps[] = [];
  const unorderedHeaders: ISwimlaneHeaderProps[] = [];

  // Add headers in saved order
  for (const id of savedOrder) {
    const header = headerMap.get(id);
    if (header) {
      orderedHeaders.push(header);
      headerMap.delete(id); // Remove to track which headers were ordered
    }
    // If ID doesn't exist in headers, silently skip it
  }

  // Second pass: Add remaining headers (not in saved order) to end
  for (const header of headerMap.values()) {
    unorderedHeaders.push(header);
  }

  return [...orderedHeaders, ...unorderedHeaders];
}
